// import { unmute } from './unmute';
import EncoderWorker from './encoderWorker?worker';
import { writeError } from '../api';
import { MicVAD } from '@ricky0123/vad-web';
import { deleteRecordings, getRecording, listRecordingIds, setRecording } from './recordings';
import { getSpeechCache } from './cache';
import captureWorklet from './capture';

export const wait = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export type Recording = { blob: Blob; buffer: BlobPart[] };

export type AudioState = 'init' | 'loading' | 'on' | 'idle' | 'mute' | 'muted';

const VAD_ASSET_BASE = 'https://cdn.jsdelivr.net/npm/@ricky0123/vad-web@0.0.30/dist/';
const ONNX_WASM_BASE = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.18.0/dist/';

export class Audio {
  context = new AudioContext();

  recorder: MicVAD | undefined;
  manualChunks: Float32Array[] = [];
  manualRecording = false;
  manualStartTime = 0;
  manualArmed = false;
  prebufferStream: MediaStream | null = null;
  prebufferSource: MediaStreamAudioSourceNode | null = null;
  prebufferNode: AudioWorkletNode | null = null;
  prebufferChunks: { time: number; data: Float32Array }[] = [];
  prebufferEnabled = false;
  prebufferWindowMs = 1000;
  error: string | null = null;
  lastActivityAt = Date.now();
  backgroundTimer: ReturnType<typeof setTimeout> | null = null;
  wasHidden = false;

  // Recording state
  state: AudioState = 'init';
  workletNode: AudioWorkletNode | null = null;
  mediaStream: MediaStreamAudioSourceNode | null = null;
  recordedAudioIds = new Set<string>();

  // Play state
  stretchNode: AudioWorkletNode | null = null;
  playingAudio: AudioBufferSourceNode | null = null;
  cachedNodes: {[key: string]: AudioBuffer } = {};
  encoderWorker: Worker;
  manualEncoderWorker: Worker;
  private listeners = new Set<() => void>();

  constructor() {
    // unmute(this.context);
    this.encoderWorker = new EncoderWorker();
    this.encoderWorker.onerror = (event) => {
      this.setError('Audio encoder error', event);
    };
    this.manualEncoderWorker = new EncoderWorker();
    this.manualEncoderWorker.onerror = (event) => {
      this.setError('Audio encoder error', event);
    };
    this.initialize();
    document.addEventListener('visibilitychange', this.handleVisibility);
    void this.hydrateRecordingIds();
  }

  async initialize(): Promise<void> {
    const { default: stretch } = await import('./stretch');
    const stretchBlob = new Blob([stretch], { type: 'application/javascript' });
    const stretchUrl = URL.createObjectURL(stretchBlob);
    await this.context.audioWorklet.addModule(stretchUrl);
    const captureBlob = new Blob([captureWorklet], { type: 'application/javascript' });
    const captureUrl = URL.createObjectURL(captureBlob);
    await this.context.audioWorklet.addModule(captureUrl);
    this.stretchNode = new AudioWorkletNode(this.context, 'stretchy');
    this.stretchNode.connect(this.context.destination);
  }

  private recordActivity(): void {
    this.lastActivityAt = Date.now();
  }

  private handleVisibility = (): void => {
    const isStandalone =
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      (navigator as any).standalone === true;
    if (document.visibilityState === 'hidden') {
      this.wasHidden = true;
      if (this.is('muted')) {
        void this.releaseAudio('background-muted');
        return;
      }
      if (isStandalone) {
        void this.releaseAudio('background-standalone');
        return;
      }
      if (this.backgroundTimer) {
        clearTimeout(this.backgroundTimer);
      }
      this.backgroundTimer = setTimeout(() => {
        if (document.visibilityState !== 'hidden') {
          return;
        }
        const idleMs = Date.now() - this.lastActivityAt;
        if (idleMs >= 60_000) {
          void this.releaseAudio('background-idle');
        }
      }, 60_000);
      return;
    }
    if (this.backgroundTimer) {
      clearTimeout(this.backgroundTimer);
      this.backgroundTimer = null;
    }
    if (this.wasHidden) {
      this.wasHidden = false;
      if (isStandalone) {
        window.location.reload();
        return;
      }
    }
    if (this.context.state === 'suspended') {
      void this.resumeAudio();
    }
  };

  private async resumeAudio(): Promise<void> {
    try {
      await this.context.resume();
    } catch {}
  }

  private async releaseAudio(reason: string): Promise<void> {
    try {
      this.playingAudio?.stop();
    } catch {}
    this.playingAudio = null;
    try {
      this.recorder?.pause();
    } catch {}
    if (this.context.state === 'running') {
      try {
        await this.context.suspend();
      } catch {}
    }
    this.set('muted');
    console.log('Audio context suspended', { reason });
  }

  private async hydrateRecordingIds(): Promise<void> {
    const ids = await listRecordingIds();
    ids.forEach((id) => this.recordedAudioIds.add(id));
  }

  hasRecording(id: string): boolean {
    return this.recordedAudioIds.has(id);
  }

  async storeRecording(id: string, file: File): Promise<void> {
    this.recordedAudioIds.add(id);
    await setRecording(id, file);
  }

  async getRecording(id: string): Promise<File | null> {
    return getRecording(id);
  }

  async purgeRecordings(ids: string[]): Promise<void> {
    ids.forEach((id) => this.recordedAudioIds.delete(id));
    await deleteRecordings(ids);
  }

  async play(
    audioUrl: string,
    options?: {
      rate?: number;
      duration?: number;
      cache?: boolean;
      context?: {
        message?: string;
        role?: string;
        eventId?: string;
      };
    }
  ): Promise<void> {
    const { rate = 1, duration, cache, context } = options ?? {};
    this.recordActivity();
    try {
      if (this.playingAudio && duration == null) {
        this.playingAudio.stop();
      }
  
      if (!this.stretchNode || audio.is('on')) {
        return;
      }

      const audioBuffer = await this.getBuffer(audioUrl, cache ?? false);

      // Fetch and decode the audio data
      if (rate === 1) {
        const a = new AudioBufferSourceNode(this.context, {
          buffer: audioBuffer,
          loop: false,
        });
        a.connect(this.context.destination);
        a.start();

        if (duration == null) {
          this.playingAudio = a;
        }

        return new Promise((resolve) => {
          setTimeout(resolve, (duration ?? audioBuffer.duration) * 1000);
        })
      }

      // Configure the worklet if needed (e.g., set rate, semitones, etc.)
      this.stretchNode.port.postMessage({
        controls: {
          rate: rate,
          semitones: 0,
          tonality: 8000,
          block: 120,
          overlap: 8,
        }
      });

      // Send audio data to the worklet for processing
      let buffers: Float32Array[] = [];
      for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
        buffers.push(audioBuffer.getChannelData(i));
      }

      // Create a buffer source node for playback
      let source = new AudioBufferSourceNode(this.context, {
        buffer: audioBuffer,
        loop: true,
      });
    
      source.connect(this.stretchNode);
    
      this.stretchNode.port.postMessage({ loopBuffers: buffers });

      // Start playback
      source.start();
      const audioDuration = audioBuffer.duration / rate;
      const audioLength = this.context.currentTime + (duration ?? audioDuration);
      source.stop(audioLength);
    
      this.playingAudio = source;

      // Handle ending of playback
      return await new Promise((resolve) => {
        source.onended = () => {
          setTimeout(() => {
            source.disconnect();
          }, (audioDuration - (duration ?? audioDuration)) * 1000);
          resolve();
        };
      });
    } catch (e) {
      writeError('Failed to play audio', {
        error: e,
        audioUrl,
        rate,
        duration,
        cache: cache ?? false,
        context,
      });
    }
  }

  private async getBuffer(audioUrl: string, cache: boolean): Promise<AudioBuffer> {
    if (this.cachedNodes[audioUrl]) {
      return this.cachedNodes[audioUrl];
    }
    try {
      const arrayBuffer = audioUrl.startsWith('speech:')
        ? (await (async () => {
            const key = audioUrl.slice('speech:'.length);
            const cachedEntry = await getSpeechCache(key);
            if (!cachedEntry) {
              throw new Error(`Missing cached speech blob for ${key}`);
            }
            return cachedEntry.buffer;
          })())
        : await (await fetch(audioUrl)).arrayBuffer();

      const audioBuffer = await this.context.decodeAudioData(arrayBuffer).catch((error) => {
        writeError('Failed to decode audio buffer', {
          error,
          audioUrl,
          byteLength: arrayBuffer.byteLength,
        });
        throw error;
      });

      if (cache) {
        this.cachedNodes[audioUrl] = audioBuffer;
      }

      return audioBuffer;
    } catch (e) {
      writeError('Failed to load audio buffer', {
        error: e,
        audioUrl,
        cache,
      });
      throw e;
    }
  }

  async stopPlaying(): Promise<void> {
    this.playingAudio?.stop();
  }

  private notify() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private setError(message: string, error?: unknown) {
    this.error = message;
    writeError(message, error);
    this.notify();
  }

  getError(): string | null {
    return this.error;
  }

  private attachStreamHandlers(stream: MediaStream | undefined) {
    if (!stream) {
      return;
    }
    for (const track of stream.getAudioTracks()) {
      track.addEventListener('ended', () => {
        this.setError('Microphone track ended');
      });
      track.addEventListener('mute', () => {
        this.setError('Microphone track muted');
      });
      track.addEventListener('unmute', () => {
        this.error = null;
        this.notify();
      });
    }
  }

  private downsampleBuffer(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
    if (outputRate >= inputRate) {
      return buffer.slice();
    }
    const ratio = inputRate / outputRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    let offsetResult = 0;
    let offsetBuffer = 0;
    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
      let accum = 0;
      let count = 0;
      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i += 1) {
        accum += buffer[i];
        count += 1;
      }
      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult += 1;
      offsetBuffer = nextOffsetBuffer;
    }
    return result;
  }

  private handlePrebufferData(data: Float32Array): void {
    const downsampled = this.downsampleBuffer(data, this.context.sampleRate, 16000);
    const now = Date.now();
    if (this.prebufferEnabled && !this.manualRecording) {
      this.prebufferChunks.push({ time: now, data: downsampled });
      const cutoff = now - this.prebufferWindowMs;
      while (this.prebufferChunks.length > 0 && this.prebufferChunks[0].time < cutoff) {
        this.prebufferChunks.shift();
      }
    }
    if (this.manualRecording) {
      if (this.manualArmed) {
        this.manualArmed = false;
        this.set('on');
      }
      this.manualChunks.push(downsampled);
    }
  }

  private async ensurePrebufferCapture(): Promise<void> {
    if (this.prebufferNode) {
      return;
    }
    this.prebufferStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.attachStreamHandlers(this.prebufferStream);
    this.prebufferSource = this.context.createMediaStreamSource(this.prebufferStream);
    this.prebufferNode = new AudioWorkletNode(this.context, 'capture-processor');
    this.prebufferNode.port.onmessage = (event) => {
      if (event.data instanceof Float32Array) {
        this.handlePrebufferData(event.data);
      } else if (event.data?.length) {
        this.handlePrebufferData(new Float32Array(event.data));
      }
    };
    this.prebufferSource.connect(this.prebufferNode);
  }

  private async startRecord(onDone: (file: File) => void): Promise<void> {
    if (!this.recorder) {
      this.encoderWorker.onmessage = (event) => {
        console.log({...event.data})
        if (event.data.type === 'finished') {
          const { buffer } = event.data as { buffer: BlobPart[] };
          const blob = new Blob(buffer, { type: 'audio/mp3' });
          const file = new File(buffer, 'audio.mp3', {
            type: blob.type,
            lastModified: Date.now(),
          })
          onDone(file);
        }
      }

      try {
        this.recorder = await MicVAD.new({
          baseAssetPath: VAD_ASSET_BASE,
          onnxWASMBasePath: ONNX_WASM_BASE,
          processorType: 'AudioWorklet',
          model: 'v5',
          preSpeechPadMs: 1200,
          minSpeechMs: 100,
          redemptionMs: 2000,
          negativeSpeechThreshold: 0.2,
          onSpeechStart: () => {
            this.set('on');
            console.log('speach started');
            this.recordActivity();
          },
          onVADMisfire: () => {
            console.log('speech misfire');
            if (this.is('on')) {
              this.set('idle');
            }
          },
          onSpeechEnd: (audio: Float32Array) => {
            console.log('speach ended')
            this.recordActivity();
            if (this.is('muted')) {
              this.recorder?.pause();
              return;
            }
            if (this.is('mute')) {
              this.recorder?.pause();
              this.set('muted');
            } else {
              this.set('loading');
            }
            this.encoderWorker.postMessage({
              command: 'encode',
              buffer: audio,
            });
          },
        });
      } catch (error) {
        this.setError('Failed to initialize microphone', error);
        this.set('muted');
        return;
      }
      this.attachStreamHandlers((this.recorder as unknown as { stream?: MediaStream }).stream);
    }

    try {
      this.recorder.start();
    } catch (error) {
      this.setError('Failed to start microphone', error);
      this.set('muted');
    }
  };

  async record(onDone: (file: File) => void): Promise<void> {
    console.log('start record');
    if (this.is('on', 'loading', 'idle', 'mute')) {
      return;
    }
    this.recordActivity();

    this.set('loading');

    const { audioSession } = (navigator as any);
    if (audioSession && audioSession.type) {
      audioSession.type = 'play-and-record';
    }

    await this.startRecord(onDone);
    await wait(50);

    this.set('idle');
  }

  // Function to stop recording audio and send it to the API
  async recordStop(): Promise<void> {
    this.recorder?.pause();
    this.set('muted');
  }

  async recordPause(): Promise<void> {
    this.recorder?.pause();
  }

  async manualRecordStart(): Promise<void> {
    if (this.is('on', 'loading', 'mute')) {
      return;
    }
    this.recordActivity();
    try {
      this.prebufferEnabled = true;
      await this.ensurePrebufferCapture();
      this.manualChunks = [];
      this.manualRecording = true;
      this.manualStartTime = Date.now();
      this.manualArmed = true;
      this.prebufferEnabled = false;
      this.set('loading');
    } catch (error) {
      this.setError('Failed to initialize microphone', error);
      this.set('muted');
    }
  }

  async manualRecordStop(): Promise<File | null> {
    if (!this.manualRecording) {
      return null;
    }
    this.manualRecording = false;
    this.manualArmed = false;
    this.set('idle');
    const cutoff = this.manualStartTime - this.prebufferWindowMs;
    const prebuffer = this.prebufferChunks
      .filter((chunk) => chunk.time >= cutoff && chunk.time <= this.manualStartTime)
      .map((chunk) => chunk.data);
    const combined = prebuffer.concat(this.manualChunks);
    this.manualChunks = [];
    if (combined.length === 0) {
      return null;
    }
    const totalLength = combined.reduce((sum, chunk) => sum + chunk.length, 0);
    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of combined) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    const file = await new Promise<File>((resolve) => {
      this.manualEncoderWorker.onmessage = (event) => {
        if (event.data.type === 'finished') {
          const { buffer } = event.data as { buffer: BlobPart[] };
          const blob = new Blob(buffer, { type: 'audio/mp3' });
          const next = new File(buffer, 'audio.mp3', {
            type: blob.type,
            lastModified: Date.now(),
          });
          resolve(next);
        }
      };
      this.manualEncoderWorker.postMessage({
        command: 'encode',
        buffer: merged,
      });
    });
    return file;
  }

  async startPrebuffer(): Promise<void> {
    if (this.prebufferEnabled || this.is('muted')) {
      return;
    }
    try {
      this.prebufferEnabled = true;
      await this.ensurePrebufferCapture();
    } catch (error) {
      this.setError('Failed to initialize microphone', error);
      this.set('muted');
    }
  }

  stopPrebuffer(): void {
    this.prebufferEnabled = false;
    this.prebufferChunks = [];
    if (this.manualRecording) {
      return;
    }
    this.prebufferNode?.disconnect();
    this.prebufferNode = null;
    this.prebufferSource?.disconnect();
    this.prebufferSource = null;
    this.prebufferStream?.getTracks().forEach((track) => track.stop());
    this.prebufferStream = null;
  }

  is(...test: AudioState[]): boolean {
    return test.includes(this.state);
  }

  getState(): AudioState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  set(state: AudioState) {
    this.state = state;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

export const audio = new Audio();
